import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import imageMerchant from '../../../assets/avatar/merchant.png';

import LoadingSkeletonModalTable from '@/components/modal/prompt/LoadingSkeletonModalTable';
import DetailUserCard from '@/components/panel/user/DetailUserCard';
import { IPaginationTable } from '@/components/table/common/interfaces/IPaginationTable';
import UserHaggleEventsTable from '@/components/table/user/UserHaggleEventsTable';
import BackButton from '@/components/ui/button/BackButton';
import {
	DEFAULT_PAGE_SIZE,
	PAGE_SIZE_OPTIONS,
} from '@/components/utils/constants';
import {
	calculateStatus,
	capitalizeAndJoin,
	formatDate,
	formatDateTime,
	isAdminCentral,
} from '@/components/utils/utils';
import { useUser } from '@/hooks/useUser';
import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import { IUser } from '@/interfaces/api/IUser';
import { ICollectionHaggleEvent } from '@/interfaces/api/haggle-event/ICollectionHaggleEvent';
import { IHaggleEvent } from '@/interfaces/api/haggle-event/IHaggleEvent';
import { IHaggleWithAction } from '@/interfaces/api/haggle-event/IHaggleWithAction';
import { UserRole } from '@/interfaces/api/user/UserRole.enum';
import { DataCard } from '@/interfaces/common/DataCard';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';

const UserDetail = () => {
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [pageNumber, setPageNumber] = useState(1);
	const { user: userAdmin } = useUser();
	const { id } = useParams<{ id: string }>();

	const {
		data: user,
		isLoading: userIsLoading,
		isError: userIsError,
		error: userError,
		refetch: userRefetch,
	} = useQuery<IUser>({
		queryKey: [ApiQueryKeys.getUserById, id],
		queryFn: () => haggleApiService.getUserById(id),
		enabled: id !== undefined && typeof id === 'string',
	});

	const isAdmin = userAdmin ? isAdminCentral(userAdmin.roles) : false;
	const hasAdmin = user?.roles.includes(UserRole.Admin);
	const showButton = isAdmin && !hasAdmin;

	const {
		data: haggleEvents,
		isLoading: haggleEventIsLoading,
		isError: haggleEventIsError,
		error: haggleEventError,
	} = useQuery<ICollectionHaggleEvent>({
		queryKey: [ApiQueryKeys.getAllHaggleEventsAdmin, pageSize, pageNumber],
		queryFn: () =>
			haggleApiService.getAllHaggleEventsAdmin({
				params: {
					'filterQuery[userId]': id,
					'page[size]': pageSize,
					'page[number]': pageNumber,
				},
			}),
		refetchOnWindowFocus: false,
	});

	if (userIsError) {
		if (userError instanceof Error) {
			notificationService.error(userError.message);
		} else {
			notificationService.error('Unknown error fetching store');
		}
	}

	if (haggleEventIsError) {
		if (haggleEventError instanceof Error) {
			notificationService.error(haggleEventError.message);
		} else {
			notificationService.error('Unknown error fetching haggle events');
		}
	}

	const handlePaginationOptions = (
		collection: ICollectionHaggleEvent,
	): IPaginationTable => {
		return {
			pageSize,
			pageNumber,
			pageCount: collection.pageCount,
			itemCount: collection.itemCount,
			pageSizeOptions: PAGE_SIZE_OPTIONS,
			setPageSize,
			setPageNumber,
		};
	};

	const columnHelper = createColumnHelper<IHaggleWithAction>();

	const columns = [
		columnHelper.accessor('id', {
			cell: (info) => <div className="w-[50px]">{info.getValue()}</div>,
			header: () => <div className="w-[50px]">ID</div>,
		}),
		{
			id: 'status',
			cell: (info: { row: { original: unknown } }) => {
				const haggleEvent = info.row.original as IHaggleEvent;
				return (
					<div className="w-[100px]">
						{calculateStatus(haggleEvent.endDate, haggleEvent.startDate)}
					</div>
				);
			},
			header: () => <div className="w-[100px]">STATUS</div>,
		},
		columnHelper.accessor('startDate', {
			cell: (info) => (
				<div className="w-[200px]">{formatDateTime(info.getValue())}</div>
			),
			header: () => <div className="w-[200px]">START DATE</div>,
		}),
		columnHelper.accessor('endDate', {
			cell: (info) => (
				<div className="w-[200px]">{formatDateTime(info.getValue())}</div>
			),
			header: () => <div className="w-[200px]">END DATE</div>,
		}),
		columnHelper.accessor('chatBots', {
			cell: (info) => {
				const agreementsReached = info
					.getValue()
					.filter((chatBot) => chatBot.agreement).length;
				return (
					<div className="w-[120px] text-center">{`${agreementsReached}/${
						info.getValue().length
					}`}</div>
				);
			},
			header: () => <div className="w-[120px] text-center">DEALS/CHATS</div>,
		}),
		columnHelper.accessor('acceptableMargin', {
			cell: (info) => (
				<div className="w-[80px] text-center">{`${info.getValue()} %`}</div>
			),
			header: () => <div className="w-[80px] text-center">MAX. DISCOUNT</div>,
		}),
		columnHelper.accessor('productIds', {
			cell: (info) => (
				<div className="w-[100px] text-right pr-[20px]">{`${
					info.getValue().length
				}`}</div>
			),
			header: () => <div className="w-[100px]">PRODUCTS</div>,
		}),
	];

	const userCard: DataCard[] = user
		? [
				{
					name: 'Date added',
					value: formatDate(user.createdAt),
				},
				{
					name: 'Roles',
					value: capitalizeAndJoin(user.roles),
				},
				{
					name: 'Haggle events',
					value: `${haggleEvents?.itemCount}`,
				},
		  ]
		: [];

	return (
		<div data-test="user-details">
			<div className="m-[0.5rem]">
				<BackButton />
			</div>
			<div className="flex flex-col gap-4">
				{userIsLoading && <LoadingSkeletonModalTable />}
				{user && !userIsLoading && (
					<DetailUserCard
						title={user.username}
						subTitle={`# ID ${user.id}`}
						avatar={imageMerchant}
						data={userCard}
						showButton={showButton}
						refetch={userRefetch}
					/>
				)}
				{haggleEvents && !haggleEventIsLoading && (
					<UserHaggleEventsTable
						collectionUserHaggleEvent={haggleEvents}
						paginationOption={handlePaginationOptions(haggleEvents)}
						columns={columns}
					/>
				)}
			</div>
		</div>
	);
};

export default UserDetail;
